import React from 'react';
import { Link } from 'react-router-dom';
import { LegalPageLayout } from '../legal/LegalPageLayout';

export function ContentPolicy() {
  return (
    <LegalPageLayout
      title="Content Policy"
      meta="Last updated: 11 August 2025 · Tarana Media Private Limited"
    >
      <section className="terms-section" id="introduction">
                    <h2 className="terms-h2">
                      1. Introduction
                    </h2>
                    <p>Our Commitment to a Safe and Creative Community</p>
                    <p >
                      The Tarana platform is an all-in-one ecosystem for music that
                      brings together Artists, Organizations, and Listeners. To ensure
                      this community remains a safe, respectful, and productive space
                      for all users, these Content Policies outline the rules and
                      standards that govern all content and behavior on the platform.
                      By using Tarana's services, all users agree to abide by these
                      policies, the Community Guidelines, and the{" "}
                      <Link to="/terms-of-use">Terms of Use</Link>.
                    </p>
                    <p >
                      The primary goal of these policies is to protect the creative
                      integrity of our users and the overall health of the community.
                      This framework is built on a foundation of user-retained
                      ownership, a clear list of prohibited content, and a transparent
                      enforcement process designed to comply with both local and
                      international regulations.
                    </p>
                  </section>
      
                  {/* 2. Content Ownership and Licensing */}
                  <section className="terms-section" id="ownership-licensing">
                    <h2 className="terms-h2">
                      2. Content Ownership and Licensing
                    </h2>
      
                    <p>
                      A cornerstone of the Tarana platform is its commitment to the
                      rights of creators. We operate on a user-centric model where
                      creators retain full ownership of their original work while
                      granting necessary licenses for the platform to function.
                    </p>
      
                    <h3 className="terms-h3">
                      2.1 Ownership of User-Generated Content (UGC)
                    </h3>
                    <p>
                      Users retain all ownership rights to their original content
                      uploaded to the platform, and Tarana explicitly does not claim
                      ownership of UGC. Content uploaded by other users remains their
                      property, and a user does not acquire ownership of another's
                      content simply by reposting it or including it in a playlist.
                    </p>
      
                    <h3 className="terms-h3">
                      2.2 Licenses Granted to Tarana and Other Users
                    </h3>
                    <p>
                      To operate its services, Tarana requires a specific license from
                      its users. By submitting content, users grant Tarana a
                      worldwide, non-exclusive, royalty-free, sublicensable, and
                      transferable license to use, reproduce, distribute, and display
                      their content in connection with the services and for marketing
                      and promotion.
                    </p>
                    <p >
                      Users also grant other users a conditional, worldwide,
                      non-exclusive license to access and use their content, but only
                      as enabled by the platform's features and sharing settings.
                    </p>
      
                    <h3 className="terms-h3">
                      2.3 Music-Specific Rights
                    </h3>
                    <p>
                      The music industry is complex, and users are solely responsible
                      for ensuring they have the necessary rights for the content they
                      upload. This includes:
                    </p>
                    <ul className="terms-list">
                      <li>
                        <strong>Master Use Rights:</strong> Pertaining to the specific
                        sound recording.
                      </li>
                      <li>
                        <strong>Public Performance Rights:</strong> For the underlying
                        musical composition (melody, lyrics).
                      </li>
                      <li>
                        <strong>Mechanical Rights:</strong> For reproducing and
                        distributing a song.
                      </li>
                      <li>
                        <strong>Synchronization Rights:</strong> For combining music
                        with visual media.
                      </li>
                    </ul>
                    <p>
                      Users must explicitly warrant that they have obtained all these
                      rights for their content, covering both the composition and the
                      master recording, for all intended uses on the platform. This
                      legal warranty is a crucial part of the{" "}
                      <Link to="/terms-of-use">Terms of Use</Link>, protecting Tarana from
                      liability in the event of an intellectual property infringement
                      claim.
                    </p>
      
                    <h3 className="terms-h3">
                      2.4 Special Rules for Bands and Freelancing
                    </h3>
                    <ul className="terms-list">
                      <li>
                        <strong>Band Content:</strong> When Content is created or
                        uploaded within a “Band,” only the credited contributors
                        retain ownership of their specific contributions. Neither
                        existing nor future Band members automatically receive rights
                        to that Content unless such rights are explicitly granted by
                        the credited contributors. In the case of Collaborative
                        Content where multiple credited contributors hold rights, it
                        is entirely up to the Band members to decide, assign, and
                        document those rights among themselves. No single contributor
                        may unilaterally grant rights to the work without the consent
                        of all credited contributors, unless such authority is defined
                        in a separate agreement outside the Platform. Existing Band
                        members who are not credited contributors may only use, copy,
                        download, modify, play, perform, or commercialize the Content
                        if the credited contributors grant such permissions.
                      </li>
                      <li>
                        <strong>Freelancing:</strong> For work delivered through the
                        freelance marketplace, the specific license for the delivered
                        work is determined by an agreement negotiated directly between
                        the Artist and the hiring party, with Tarana acting only as a
                        facilitator.
                      </li>
                    </ul>
      
                    <h3 className="terms-h3">
                      2.5 Indemnification: User Responsibility for Legal Actions
                    </h3>
                    <p>
                      By submitting content, users agree to indemnify, defend, and
                      hold Tarana, its affiliates, directors, officers, employees, and
                      agents harmless from any and all claims, damages, losses, and
                      expenses, including attorneys' fees and costs, that arise from
                      or are related to their content. This means that any legal
                      action resulting from a user's uploaded content is the sole
                      responsibility of the user, and they are contractually obligated
                      to protect Tarana from such claims.
                    </p>
                  </section>
      
                  {/* 3. Prohibited Content and Behavior */}
                  <section className="terms-section" id="prohibited-content">
                    <h2 className="terms-h2">
                      3. Prohibited Content and Behavior
                    </h2>
                    <p>
                      Tarana maintains a strict policy against several categories of
                      content and behavior that are harmful, illegal, or disruptive to
                      the community. These standards apply to all user-generated
                      content, including music, videos, comments, and profile
                      information.
                    </p>
      
                    <h3 className="terms-h3">
                      3.1 Hate Speech and Harassment
                    </h3>
                    <p>
                      Zero-tolerance for content that promotes violence, incites
                      hatred, or disparages individuals or groups based on protected
                      characteristics (race, ethnicity, caste, sexual orientation,
                      gender, religion, age, disability, serious disease, etc.).
                      Examples include dehumanizing statements, promoting hate groups,
                      and targeted harassment.
                    </p>
      
                    <h3 className="terms-h3">
                      3.2 Violence, Threats, and Graphic Content
                    </h3>
                    <p>
                      CContent that depicts, glorifies, or incites violence, or that
                      encourages serious physical harm, is strictly prohibited. This
                      includes:
                    </p>
                    <ul className="terms-list">
                      <li>
                        Threats of serious physical harm or acts of violence against a
                        specific target or group.
                      </li>
                      <li>
                        Content that encourages, promotes, or glorifies suicide and
                        self-harm.
                      </li>
                      <li>
                        Graphic or gratuitous depictions of violence, gore, or other
                        shocking imagery.
                      </li>
                    </ul>
      
                    <h3 className="terms-h3">
                      3.3 Sexually Explicit and Obscene Material
                    </h3>
                    <p>
                      The platform prohibits all sexually explicit and obscene
                      material. This includes:
                    </p>
                    <ul className="terms-list">
                      <li>
                        Pornography or visual depictions of genitalia or nudity
                        presented for sexual gratification.
                      </li>
                      <li>
                        Content that advocates for, glorifies, or promotes sexual themes
                        related to rape, incest, or bestiality.
                      </li>
                      <li>
                        A zero-tolerance policy for all content related to child sexual
                        abuse material (CSAM).
                      </li>
                    </ul>
                    <h3 className="terms-h3">
                      3.4 Illegal Activities and Regulated Goods
                    </h3>
                    <p>
                      Tarana has a zero-tolerance policy for illegal activities. The
                      platform may not be used to promote or commit any illegal act,
                      nor to promote the sale of illegal or regulated goods or
                      services such as illegal firearms, illegal drugs, or endangered
                      species.
                    </p>
      
                    <h3 className="terms-h3">
                      3.5 Intellectual Property Infringement
                    </h3>
                    <p>
                      The platform strictly prohibits "Infringing Content," which is
                      defined as material that violates the copyrights or trademarks
                      of others. All users are responsible for ensuring they have the
                      right to upload their content, and any infringing material will
                      be subject to removal.
                    </p>
      
                    <h3 className="terms-h3">
                      3.6 Spam, Fraud, and Deceptive Practices
                    </h3>
                    <p>
                      To maintain platform integrity and user trust, any form of
                      fraud, deception, or spam is prohibited. This includes:
                    </p>
                    <ul className="terms-list">
                      <li>
                        <strong>Impersonation:</strong> Posing as another person,
                        brand, or organization in a way that is intended or likely to
                        deceive.
                      </li>
                      <li>
                        <strong>Fake Engagement:</strong> Using automated means (e.g.,
                        bots, scripts) to artificially inflate play counts, downloads,
                        ratings, or reviews, or offering or accepting compensation for
                        these purposes.
                      </li>
                      <li>
                        <strong>Misinformation and Scams:</strong> Posting content
                        that is misleading, deceptive, or intended to scam or defraud
                        other users, including phishing attempts or malicious links.
                      </li>
                    </ul>
      
                    <h3 className="terms-h3">
                      3.7 Privacy Violations
                    </h3>
                    <p>
                      Users must respect the privacy of others. The guidelines
                      strictly prohibit content that is "invasive of another
                      individual's privacy," including sharing non-consensual intimate
                      content or another person's private personal details (e.g.,
                      credit card numbers, unlisted phone numbers, private
                      conversations, or health information) without their explicit
                      permission.
                    </p>
                  </section>
      
                  {/* 4. Content Moderation and Enforcement */}
                  <section className="terms-section" id="moderation-enforcement">
                    <h2 className="terms-h2">
                      4. Content Moderation and Enforcement
                    </h2>
      
                    <p>
                      Tarana employs a robust, multi-layered approach to content
                      moderation to enforce these policies fairly and consistently.
                    </p>
      
                    <h3 className="terms-h3">
                      4.1 Our Moderation Approach
                    </h3>
                    <ul className="terms-list">
                      <li>
                        <strong>User Reporting:</strong> Tarana provides clear and
                        accessible mechanisms for users to report content they believe
                        violates the policies.
                      </li>
                      <li>
                        <strong>Human Moderation:</strong>A dedicated team of human
                        moderators reviews all flagged content and user reports to
                        make final decisions based on the context and a consistent
                        interpretation of the guidelines.
                      </li>
                    </ul>
      
                    <h3 className="terms-h3">
                      4.2 Consequences for Violations
                    </h3>
                    <p>
                      Tarana uses a tiered system of penalties based on the severity
                      and frequency of the violation. The consequences can include:
                    </p>
                    <ul className="terms-list">
                      <li>
                        <strong>Content Removal:</strong> For most first-time and
                        minor violations, the offending content will be removed, and
                        the user may receive a warning.
                      </li>
                      <li>
                        <strong>Temporary Account Suspension: </strong>For repeated
                        policy violations or a serious first-time offense, a user’s
                        account may be temporarily suspended.
                      </li>
                      <li>
                        <strong>Permanent Account Termination:</strong> Severe or
                        repeated violations will result in the permanent termination
                        of the user’s account, with a loss of access to funds,
                        features, and uploaded content.
                      </li>
                    </ul>
                  </section>
      
                  {/* 5. Appendix: Prohibited Content Table */}
                  <section className="terms-section" id="appendix">
                    <h2 className="terms-h2">
                      5. Appendix: Prohibited Content Table
                    </h2>
      
                    <div className="terms-table-wrap">
                      <table className="terms-table">
                        <thead >
                          <tr>
                            <th className="terms-table-cell">
                              Category
                            </th>
                            <th className="terms-table-cell">
                              Description
                            </th>
                            <th className="terms-table-cell">
                              Examples of Prohibited Content
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="terms-table-cell">
                              Hate Speech & Harassment
                            </td>
                            <td className="terms-table-cell">
                              Content that promotes violence or incites hatred against
                              individuals or groups based on protected
                              characteristics.
                            </td>
                            <td className="terms-table-cell">
                              Dehumanizing statements; promoting supremacist
                              ideologies; using hateful symbols; targeting a group
                              based on race or religion
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Violence & Threats
                            </td>
                            <td className="terms-table-cell">
                              Content that glorifies, incites, or threatens serious
                              physical harm or death.
                            </td>
                            <td className="terms-table-cell">
                              Threats of violence against a specific person; content
                              that encourages suicide or self-harm; promoting animal
                              cruelty
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Graphic Content
                            </td>
                            <td className="terms-table-cell">
                              Gratuitous or shocking depictions of violence, gore, or
                              injury.
                            </td>
                            <td className="terms-table-cell">
                              Videos of graphic violence or abuse; depictions of
                              severely mutilated bodies; gruesome images of trauma
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Sexually Explicit
                            </td>
                            <td className="terms-table-cell">
                              Material that is pornographic or depicts sexual acts for
                              sexual gratification
                            </td>
                            <td className="terms-table-cell">
                              Pornography; depictions of genitalia or nudity for
                              sexual gratification; content that glorifies sexual
                              violence
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              IP Infringement
                            </td>
                            <td className="terms-table-cell">
                              Material that violates copyrights, trademarks, or other
                              intellectual property rights
                            </td>
                            <td className="terms-table-cell">
                              Uploading a song you don't have rights to; using a
                              brand's logo without permission; using uncleared samples
                              in your music
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Spam & Fraud
                            </td>
                            <td className="terms-table-cell">
                              Content that is misleading, deceptive, or intended to
                              scam or defraud others
                            </td>
                            <td className="terms-table-cell">
                              Fake engagement (bots, scripts); phishing links;
                              impersonating an artist or brand; soliciting payments
                              outside the platform
                            </td>
                          </tr>
                          <tr>
                            <td className="terms-table-cell">
                              Privacy Violations
                            </td>
                            <td className="terms-table-cell">
                              Sharing another person's private information without
                              their consent
                            </td>
                            <td className="terms-table-cell">
                              Sharing unlisted phone numbers; posting non-consensual
                              intimate content; sharing private health information
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </section>
      
                  <p className="text-sm text-gray-500">
                    <strong>Last Updated:</strong> August 11, 2025
                  </p>
    </LegalPageLayout>
  );
}
